import React, { useState, useEffect } from 'react';
import GooglePlacesAutocomplete from './GooglePlacesAutocomplete';

// Common countries with ISO 3166-1 alpha-2 codes
const COUNTRIES = [
  { code: 'in', name: 'India' },
  { code: 'us', name: 'United States' },
  { code: 'gb', name: 'United Kingdom' },
  { code: 'ca', name: 'Canada' },
  { code: 'au', name: 'Australia' },
  { code: 'nz', name: 'New Zealand' },
  { code: 'ae', name: 'United Arab Emirates' },
  { code: 'sg', name: 'Singapore' },
  { code: 'my', name: 'Malaysia' },
  { code: 'ph', name: 'Philippines' },
  { code: 'sa', name: 'Saudi Arabia' },
  { code: 'pk', name: 'Pakistan' },
  { code: 'bd', name: 'Bangladesh' },
  { code: 'lk', name: 'Sri Lanka' },
  { code: 'np', name: 'Nepal' },
  { code: 'de', name: 'Germany' },
  { code: 'fr', name: 'France' },
  { code: 'it', name: 'Italy' },
  { code: 'es', name: 'Spain' },
  { code: 'nl', name: 'Netherlands' },
  { code: 'be', name: 'Belgium' },
  { code: 'ch', name: 'Switzerland' },
  { code: 'at', name: 'Austria' },
  { code: 'se', name: 'Sweden' },
  { code: 'no', name: 'Norway' },
  { code: 'dk', name: 'Denmark' },
  { code: 'fi', name: 'Finland' },
  { code: 'pl', name: 'Poland' },
  { code: 'cz', name: 'Czech Republic' },
  { code: 'ie', name: 'Ireland' },
  { code: 'pt', name: 'Portugal' },
  { code: 'gr', name: 'Greece' },
  { code: 'ru', name: 'Russia' },
  { code: 'cn', name: 'China' },
  { code: 'jp', name: 'Japan' },
  { code: 'kr', name: 'South Korea' },
  { code: 'th', name: 'Thailand' },
  { code: 'vn', name: 'Vietnam' },
  { code: 'id', name: 'Indonesia' },
  { code: 'za', name: 'South Africa' },
  { code: 'eg', name: 'Egypt' },
  { code: 'ke', name: 'Kenya' },
  { code: 'ng', name: 'Nigeria' },
  { code: 'br', name: 'Brazil' },
  { code: 'mx', name: 'Mexico' },
  { code: 'ar', name: 'Argentina' },
  { code: 'cl', name: 'Chile' },
  { code: 'co', name: 'Colombia' },
  { code: 'pe', name: 'Peru' },
];

interface AddressInputProps {
  value?: {
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  onChange: (address: any) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string;
  apiKey?: string;
  countryRestrictions?: string[];
  defaultCountry?: string; // ISO 3166-1 alpha-2 code (e.g., 'in', 'us')
}

export const AddressInput: React.FC<AddressInputProps> = ({
  value = {},
  onChange,
  placeholder = 'Enter address',
  disabled = false,
  className = '',
  label,
  required = false,
  error,
  apiKey,
  countryRestrictions, // Deprecated - will use selectedCountry instead
  defaultCountry = 'in' // Default to India
}) => {
  const [addressData, setAddressData] = useState(value);
  // State to track if address has been selected or manual entry is active
  const [showDetails, setShowDetails] = useState(() => {
    // Show details if we already have address data
    return !!(value.address || value.city || value.state || value.postalCode);
  });
  // State for selected country code (ISO 3166-1 alpha-2)
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(() => {
    // If country is already set in value, try to find matching code
    if (value.country) {
      const country = COUNTRIES.find(c => 
        c.name.toLowerCase() === value.country?.toLowerCase()
      );
      if (country) return country.code;
    }
    return defaultCountry;
  });

  // Update selected country when value.country changes externally
  useEffect(() => {
    if (value.country) {
      const country = COUNTRIES.find(c => 
        c.name.toLowerCase() === value.country?.toLowerCase()
      );
      if (country && country.code !== selectedCountryCode) {
        setSelectedCountryCode(country.code);
      }
    }
  }, [value.country, selectedCountryCode]);

  const handleAddressChange = (address: string, placeDetails?: any) => {
    // Always preserve the address text, even if placeDetails is provided
    // Use the formatted address from placeDetails if available, otherwise use the passed address
    const finalAddress = placeDetails?.formatted_address || placeDetails?.displayName || address || addressData.address;
    const newAddressData = { ...addressData, address: finalAddress };
    
    // Show details if address is selected or manually entered
    if (finalAddress && finalAddress.trim().length > 0) {
      setShowDetails(true);
    }

    if (placeDetails) {
      console.log('🔍 Place details received:', placeDetails);
      console.log('🔍 addressComponents:', placeDetails.addressComponents);
      console.log('🔍 address_components:', placeDetails.address_components);
      
      // Handle new Place API format first (addressComponents with longText/shortText)
      if (placeDetails.addressComponents && Array.isArray(placeDetails.addressComponents)) {
        // New Place API format (camelCase, longText/shortText)
        const components = placeDetails.addressComponents;
        
        // Helper function to get component by type
        const getComponent = (types: string[]) => {
          return components.find((comp: any) => 
            comp.types && Array.isArray(comp.types) && types.some(type => comp.types.includes(type))
          );
        };

        // Extract city
        const cityComponent = getComponent(['locality', 'administrative_area_level_2']);
        if (cityComponent) {
          newAddressData.city = cityComponent.longText || cityComponent.shortText || '';
          console.log('✅ City extracted:', newAddressData.city);
        }

        // Extract state
        const stateComponent = getComponent(['administrative_area_level_1']);
        if (stateComponent) {
          newAddressData.state = stateComponent.longText || stateComponent.shortText || '';
          console.log('✅ State extracted:', newAddressData.state);
        }

        // Extract postal code
        const postalComponent = getComponent(['postal_code']);
        if (postalComponent) {
          newAddressData.postalCode = postalComponent.longText || postalComponent.shortText || '';
          console.log('✅ Postal code extracted:', newAddressData.postalCode);
        }

        // Extract country
        const countryComponent = getComponent(['country']);
        if (countryComponent) {
          newAddressData.country = countryComponent.longText || countryComponent.shortText || '';
          console.log('✅ Country extracted:', newAddressData.country);
          // Update selected country code if we found a match
          const country = COUNTRIES.find(c => 
            c.name.toLowerCase() === newAddressData.country.toLowerCase() ||
            c.code.toLowerCase() === countryComponent.shortText?.toLowerCase()
          );
          if (country) {
            setSelectedCountryCode(country.code);
          }
        }

        // Extract coordinates (check both new format and geometry)
        if (placeDetails.coordinates) {
          newAddressData.coordinates = placeDetails.coordinates;
        } else if (placeDetails.geometry && placeDetails.geometry.location) {
          newAddressData.coordinates = {
            lat: typeof placeDetails.geometry.location.lat === 'function' 
              ? placeDetails.geometry.location.lat()
              : placeDetails.geometry.location.lat,
            lng: typeof placeDetails.geometry.location.lng === 'function'
              ? placeDetails.geometry.location.lng()
              : placeDetails.geometry.location.lng
          };
        }
      } else if (placeDetails.address_components && Array.isArray(placeDetails.address_components)) {
        // Old API format
        const components = placeDetails.address_components;
        
        // Helper function to get component by type
        const getComponent = (types: string[]) => {
          return components.find((comp: any) => 
            types.some(type => comp.types.includes(type))
          );
        };

        // Extract city
        const cityComponent = getComponent(['locality', 'administrative_area_level_2']);
        if (cityComponent) {
          newAddressData.city = cityComponent.long_name;
        }

        // Extract state
        const stateComponent = getComponent(['administrative_area_level_1']);
        if (stateComponent) {
          newAddressData.state = stateComponent.long_name;
        }

        // Extract postal code
        const postalComponent = getComponent(['postal_code']);
        if (postalComponent) {
          newAddressData.postalCode = postalComponent.long_name;
        }

        // Extract country
        const countryComponent = getComponent(['country']);
        if (countryComponent) {
          newAddressData.country = countryComponent.long_name;
          // Update selected country code if we found a match
          const country = COUNTRIES.find(c => 
            c.name.toLowerCase() === countryComponent.long_name.toLowerCase() ||
            c.code.toLowerCase() === countryComponent.short_name?.toLowerCase()
          );
          if (country) {
            setSelectedCountryCode(country.code);
          }
        }

        // Extract coordinates
        if (placeDetails.geometry && placeDetails.geometry.location) {
          newAddressData.coordinates = {
            lat: typeof placeDetails.geometry.location.lat === 'function' 
              ? placeDetails.geometry.location.lat()
              : placeDetails.coordinates?.lat,
            lng: typeof placeDetails.geometry.location.lng === 'function'
              ? placeDetails.geometry.location.lng()
              : placeDetails.coordinates?.lng
          };
        }
      }
    }

    setAddressData(newAddressData);
    onChange(newAddressData);
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountryCode = e.target.value;
    setSelectedCountryCode(newCountryCode);
    const selectedCountry = COUNTRIES.find(c => c.code === newCountryCode);
    if (selectedCountry) {
      const newData = { ...addressData, country: selectedCountry.name };
      setAddressData(newData);
      onChange(newData);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Country Selector - Shown first so user can select country before address */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Country
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <select
          value={selectedCountryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed bg-white text-gray-900"
        >
          {COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          Select your country first to get better address suggestions
        </p>
      </div>

      {/* Main Address Input with Google Places Autocomplete */}
      <GooglePlacesAutocomplete
        value={addressData.address || ''}
        onChange={handleAddressChange}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        apiKey={apiKey}
        countryRestrictions={[selectedCountryCode]} // Use selected country for autocomplete bias
        types={['street_address', 'premise', 'subpremise']}
        onManualEntryToggle={(isManual) => {
          // Show details when manual entry is activated
          if (isManual) {
            setShowDetails(true);
          }
        }}
      />

      {/* Show selected address if available */}
      {showDetails && addressData.address && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-600 font-medium mb-1">Selected Address:</p>
          <p className="text-sm text-gray-800">{addressData.address}</p>
        </div>
      )}

      {/* Address Details Grid - Only show after address is selected or manual entry */}
      {showDetails && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City
          </label>
          <input
            type="text"
            value={addressData.city || ''}
            onChange={(e) => {
              const newData = { ...addressData, city: e.target.value };
              setAddressData(newData);
              onChange(newData);
            }}
            placeholder="City"
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed bg-white text-gray-900"
          />
        </div>

        {/* State */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State
          </label>
          <input
            type="text"
            value={addressData.state || ''}
            onChange={(e) => {
              const newData = { ...addressData, state: e.target.value };
              setAddressData(newData);
              onChange(newData);
            }}
            placeholder="State"
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed bg-white text-gray-900"
          />
        </div>

        {/* Postal Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Postal Code
          </label>
          <input
            type="text"
            value={addressData.postalCode || ''}
            onChange={(e) => {
              const newData = { ...addressData, postalCode: e.target.value };
              setAddressData(newData);
              onChange(newData);
            }}
            placeholder="Postal Code"
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed bg-white text-gray-900"
          />
        </div>

        {/* Country Display (read-only, set by dropdown above) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country (auto-filled)
          </label>
          <input
            type="text"
            value={addressData.country || COUNTRIES.find(c => c.code === selectedCountryCode)?.name || ''}
            readOnly
            disabled={disabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
          />
        </div>
        </div>
      )}

      {/* Coordinates Display (for debugging) */}
      {addressData.coordinates && (
        <div className="text-xs text-gray-500">
          Coordinates: {addressData.coordinates.lat.toFixed(6)}, {addressData.coordinates.lng.toFixed(6)}
        </div>
      )}
    </div>
  );
};

export default AddressInput;


